import React, {memo} from 'react';
import {Cell, Row} from 'react-table';
import styled, {css} from 'styled-components';

import {HandlerOf, PredicateOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import {RandomSkeleton} from '../../../components/common/randomSkeleton';
import {isColumnNumerical, isCustomTableColumn} from '../reportElements/table/tableHelpers';
import {stickyColumn} from './tableCommons';

/*
 * Const.
 */

const TEXT_CELL_WIDTH = 220;
const CELL_PADDING = 12;

/*
 * Props.
 */

interface TableRowProps<T extends object> {
  prepareRow: HandlerOf<Row<T>>;
  row: Row<T>;
  isLoading?: boolean;
  isFirstColumnSticky?: boolean;
  hasOverflow?: boolean;
  hasScrolled?: boolean;
  onCellSelect?: HandlerOf<Cell<T>>;
  isCellSelectable?: PredicateOf<Cell<T>>;
}

/*
 * Styles.
 */

const StyledBodyRow = styled.tr`
  background-color: ${(p) => p.theme.foreground};
  :hover {
    background-color: ${(p) => p.theme.greys.shade10};
  }
`;

const StyledBodyCell = styled.td<{
  $isNumerical: boolean;
  $isSticky: boolean;
  $hasClickListener: boolean;
}>`
  /* Make all numerical columns take as little space as possible. Text columns take all the remaining space, with a min-width.*/
  min-width: ${(p) => (p.$isNumerical ? `1%` : `${TEXT_CELL_WIDTH}px`)};
  width: ${(p) => (p.$isNumerical ? `1%` : '100%')};
  padding: ${CELL_PADDING}px ${CELL_PADDING}px ${CELL_PADDING - 1}px ${CELL_PADDING}px;

  text-align: ${(p) => (p.$isNumerical ? 'right' : 'left')};
  font-weight: ${fontWeights.normal};
  border-bottom: 1px solid ${(p) => p.theme.greys.shade30};

  ${(p) => p.$isSticky && stickyColumn}
  ${(p) =>
    p.$hasClickListener &&
    css`
      :hover {
        color: ${p.theme.interactiveText};
      }
    `}
`;

// Adding a sub-layer to make sure the cell content doesn't go above TEXT_CELL_WIDTH.
// (to keep the text cell content consistent in size between pages even though the cell can take more than TEXT_CELL_WIDTH).
const StyledCellContent = styled.div`
  cursor: inherit;
  max-width: ${TEXT_CELL_WIDTH - CELL_PADDING * 2}px;
  ${ellipsis()};
`;

/*
 * Component.
 */

const TableRowComponent = <T extends Object>({
  prepareRow,
  row,
  isLoading,
  isFirstColumnSticky,
  hasOverflow,
  hasScrolled,
  onCellSelect,
  isCellSelectable,
}: TableRowProps<T>) => {
  // Prepare the row for display
  prepareRow(row);

  return (
    // Apply the row props
    <StyledBodyRow {...row.getRowProps()}>
      {
        // Loop over the rows cells
        row.cells.map((cell) => {
          const {column} = cell;
          const isNumerical = isCustomTableColumn(column) ? isColumnNumerical(column.type) : false;
          const isFirstColumn = column.index === 0;
          const isSticky = Boolean(isFirstColumnSticky && isFirstColumn && hasOverflow && hasScrolled);

          // If isCellSelectable is provided, cell is clickable if it returns true, otherwise it's clickable if we have an onCellSelect handler.
          const hasSelectionHandler = typeof onCellSelect === 'function';
          const hasClickListener =
            hasSelectionHandler && isCellSelectable ? isCellSelectable(cell) : hasSelectionHandler;

          return (
            // Apply the cell props
            <StyledBodyCell
              {...cell.getCellProps()}
              $isNumerical={isNumerical}
              $isSticky={isSticky}
              $hasClickListener={hasClickListener}
              onClick={() => hasClickListener && handleCellClick(cell, onCellSelect)}
            >
              <StyledCellContent>{isLoading ? <RandomSkeleton /> : cell.render('Cell')}</StyledCellContent>
            </StyledBodyCell>
          );
        })
      }
    </StyledBodyRow>
  );
};

export const TableRow = memo(TableRowComponent) as typeof TableRowComponent;

/*
 * Helpers.
 */

function handleCellClick<T extends object>(cell: Cell<T>, onCellSelect?: HandlerOf<Cell<T>>) {
  if (!onCellSelect) {
    return;
  }

  onCellSelect(cell);
}
